import React, { useEffect, useState } from 'react';
import { Box, BoxProps, CircularProgress, Typography } from '@mui/material';

import {
  type DevLoadingTraceDetails,
  toDevLoadingTraceKey,
  useDevLoadingTrace,
} from '@/utils/devLoadingTrace';

import { STUDENT_LONG_WAIT_MS } from './StudentContentBoundary';

/** Said after the long wait, when the page still hasn't arrived. */
export const LONG_WAIT_MESSAGE =
  'This is taking longer than usual. Check your connection or reload the page.';

interface LoadingSpinnerProps
  extends Omit<BoxProps, 'display' | 'justifyContent' | 'alignItems'> {
  minHeight?: string;
  traceKey?: string;
  traceDetails?: DevLoadingTraceDetails;
  /**
   * Words under the spinner ("Loading…"), for a wait that fills the page. With
   * a label the spinner becomes a polite status region and, after the same
   * eight seconds student loading uses, adds what to do about a slow load.
   */
  label?: string;
  /** Tests may shorten this; product surfaces keep the eight-second contract. */
  longWaitMs?: number;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  minHeight = '200px',
  traceKey,
  traceDetails,
  label,
  longWaitMs = STUDENT_LONG_WAIT_MS,
  ...boxProps
}) => {
  useDevLoadingTrace(traceKey ?? 'shared.loading-spinner', true, {
    minHeight,
    component: 'LoadingSpinner',
    traceKey: traceKey ? toDevLoadingTraceKey(traceKey) : undefined,
    ...traceDetails,
  });

  const [takingLonger, setTakingLonger] = useState(false);
  useEffect(() => {
    if (!label) return undefined;
    const timeout = window.setTimeout(() => setTakingLonger(true), longWaitMs);
    return () => window.clearTimeout(timeout);
  }, [label, longWaitMs]);

  if (!label) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={minHeight}
        {...boxProps}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      gap="var(--sp-sm)"
      minHeight={minHeight}
      role="status"
      aria-live="polite"
      {...boxProps}
    >
      <CircularProgress aria-hidden />
      <Typography
        sx={{ color: 'var(--ink-muted)', fontSize: 14, fontWeight: 600 }}
      >
        {label}
      </Typography>
      {takingLonger && (
        <Typography
          sx={{
            color: 'var(--ink-muted)',
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'center',
            maxWidth: 360,
            px: 2,
          }}
        >
          {LONG_WAIT_MESSAGE}
        </Typography>
      )}
    </Box>
  );
};

export default LoadingSpinner;
